import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  Stack,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import { Organization, PaymentMethod } from '../../models/Organization';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/AuthService';
import {
  resolveStripeConnectStatus,
  type StripeConnectStatus,
} from '../../utils/stripeConnectStatus';

const OrganizationsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assumedOrg] = useState(authService.getAssumedOrganization());
  const [updatingEnabledStatus, setUpdatingEnabledStatus] = useState<string | null>(null);
  const [updatingExperimentalOrgId, setUpdatingExperimentalOrgId] = useState<string | null>(null);
  const [updatingTestOrgId, setUpdatingTestOrgId] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const data = await adminApiClient.getOrganizations();
        // Ensure data is an array before processing
        if (!Array.isArray(data)) {
          console.error('Expected array but got:', typeof data, data);
          setOrganizations([]);
          setError(t('organizations.fetchError', 'Invalid data format received'));
          return;
        }
        // Filter out "Band Space Org" and ensure each organization has an id field for DataGrid
        const organizationsWithIds = data
          .filter((org: any) => {
            if (!org) return false;
            // Check both name and id fields to filter out "Band Space Org"
            return org.name !== 'Band Space Org' && org.id !== 'Band Space Org';
          })
          .map((org: any, index: number) => ({
            ...org,
            id: org.id || index // fallback to index if no id
          }));
        setOrganizations(organizationsWithIds);
      } catch (err) {
        setError(t('organizations.fetchError', 'Failed to fetch organizations'));
        console.error('Error fetching organizations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [t]);

  const handleSwitchTo = (organizationId: string) => {
    const organization = organizations.find(org => org.id === organizationId);
    if (organization) {
      authService.setAssumedOrganization(organizationId, organization.name);
      navigate('/host/organization');
      // Force page reload to ensure all components are re-rendered with new state
      window.location.reload();
    }
  };

  const handleSwitchBack = () => {
    authService.clearAssumedOrganization();
    window.location.reload();
  };

  const handleToggleEnabled = async (organizationId: string, currentEnabled: boolean) => {
    try {
      setUpdatingEnabledStatus(organizationId);
      await adminApiClient.updateOrganizationEnabledStatus(organizationId, !currentEnabled);
      setOrganizations(prev => prev.map(org => 
        org.id === organizationId 
          ? { ...org, enabled: !currentEnabled }
          : org
      ));
    } catch (err) {
      setError(t('organizations.updateEnabledError', 'Failed to update organization enabled status'));
      console.error('Error updating organization enabled status:', err);
    } finally {
      setUpdatingEnabledStatus(null);
    }
  };

  const handleToggleExperimental = async (organizationId: string, currentlyOn: boolean) => {
    try {
      setUpdatingExperimentalOrgId(organizationId);
      if (currentlyOn) {
        await adminApiClient.disableOrganizationExperimentalFeatures(organizationId);
      } else {
        await adminApiClient.enableOrganizationExperimentalFeatures(organizationId);
      }
      setOrganizations(prev =>
        prev.map(org =>
          org.id === organizationId
            ? { ...org, experimentalFeaturesEnabled: !currentlyOn }
            : org
        )
      );
    } catch (err) {
      setError(
        t(
          'organizations.updateExperimentalError',
          'Failed to update experimental features for this organization'
        )
      );
      console.error('Error updating organization experimental features:', err);
    } finally {
      setUpdatingExperimentalOrgId(null);
    }
  };

  const handleToggleTest = async (organizationId: string, currentlyOn: boolean) => {
    try {
      setUpdatingTestOrgId(organizationId);
      await adminApiClient.updateOrganizationTestStatus(organizationId, !currentlyOn);
      setOrganizations(prev =>
        prev.map(org =>
          org.id === organizationId
            ? { ...org, test: !currentlyOn }
            : org
        )
      );
    } catch (err) {
      setError(t('organizations.updateTestError', 'Failed to update test status for this organization'));
      console.error('Error updating organization test status:', err);
    } finally {
      setUpdatingTestOrgId(null);
    }
  };

  const stripeStatusLabel = (status: StripeConnectStatus): string => {
    switch (status) {
      case 'fully_connected':
        return t('organizations.stripeConnect.connected', 'Connected');
      case 'action_required':
        return t('organizations.stripeConnect.actionRequired', 'Action required');
      case 'onboarding_required':
        return t('organizations.stripeConnect.onboardingRequired', 'Onboarding');
      case 'pending_verification':
        return t('organizations.stripeConnect.pendingVerification', 'Pending');
      case 'not_connected':
      default:
        return t('organizations.stripeConnect.notConnected', 'Not connected');
    }
  };

  const stripeStatusColor = (
    status: StripeConnectStatus,
  ): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'fully_connected':
        return 'success';
      case 'action_required':
      case 'pending_verification':
        return 'warning';
      case 'onboarding_required':
        return 'info';
      case 'not_connected':
      default:
        return 'default';
    }
  };

  const formatPaymentMethods = (methods: PaymentMethod[] | undefined): string => {
    if (!methods || methods.length === 0) {
      return t('organizations.paymentMethods.none', 'None');
    }
    return methods
      .map((method) =>
        method === PaymentMethod.ONLINE
          ? t('organizations.paymentMethods.online', 'Online')
          : t('organizations.paymentMethods.cash', 'Cash'),
      )
      .join(', ');
  };

  const renderStripeConnectCell = (org: Organization) => {
    const status = resolveStripeConnectStatus(org);
    const paymentMethodsLabel = formatPaymentMethods(org.supportedPaymentMethods);
    const tooltipParts = [
      org.stripeConnectExpressAccountId
        ? t('organizations.stripeConnect.accountId', 'Account: {{id}}', {
            id: org.stripeConnectExpressAccountId,
          })
        : null,
      t('organizations.paymentMethods.label', 'Payment methods: {{methods}}', {
        methods: paymentMethodsLabel,
      }),
    ].filter(Boolean);

    return (
      <Tooltip title={tooltipParts.join(' · ')} arrow>
        <Chip
          size="small"
          label={stripeStatusLabel(status)}
          color={stripeStatusColor(status)}
          variant={status === 'not_connected' ? 'outlined' : 'filled'}
          sx={{ fontWeight: 500 }}
        />
      </Tooltip>
    );
  };

  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: t('organizations.name', 'Name'), 
      flex: 2,
      renderCell: (params: any) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.name}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'enabled', 
      headerName: t('organizations.enabled', 'Enabled'), 
      width: 100,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: (params: any) => {
        const isUpdating = updatingEnabledStatus === params.row.id;
        return (
          <Switch
            checked={params.row.enabled ?? true}
            onChange={() => {
              handleToggleEnabled(params.row.id, params.row.enabled ?? true);
            }}
            disabled={isUpdating}
            inputProps={{
              'aria-label': t('organizations.enabled', 'Enabled'),
            }}
          />
        );
      }
    },
    {
      field: 'experimentalFeaturesEnabled',
      headerName: t('organizations.experimental', 'Experimental'),
      width: 100,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: (params: any) => {
        const isUpdating = updatingExperimentalOrgId === params.row.id;
        const on = params.row.experimentalFeaturesEnabled === true;
        return (
          <Switch
            checked={on}
            onChange={() => handleToggleExperimental(params.row.id, on)}
            disabled={isUpdating}
            inputProps={{
              'aria-label': t('organizations.experimental', 'Experimental'),
            }}
          />
        );
      },
    },
    {
      field: 'test',
      headerName: t('organizations.test', 'Test'),
      width: 100,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: (params: any) => {
        const isUpdating = updatingTestOrgId === params.row.id;
        const on = params.row.test === true;
        return (
          <Switch
            checked={on}
            onChange={() => handleToggleTest(params.row.id, on)}
            disabled={isUpdating}
            inputProps={{
              'aria-label': t('organizations.test', 'Test'),
            }}
          />
        );
      },
    },
    {
      field: 'stripeConnect',
      headerName: t('organizations.stripeConnect.column', 'Stripe Connect'),
      width: 160,
      sortable: false,
      renderCell: (params: any) => renderStripeConnectCell(params.row as Organization),
    },
    { 
      field: 'createdAt', 
      headerName: t('organizations.createdAt', 'Created At'), 
      flex: 1,
      renderCell: (params: any) => {
        const date = params.row?.createdAt;
        if (!date) return '';
        try {
          return format(new Date(date), 'MMM d, yyyy HH:mm');
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params: any) => {
        const isAssumedOrg = params.row.id === assumedOrg.id;
        return (
          <Button
            variant={isAssumedOrg ? "contained" : "outlined"}
            color={isAssumedOrg ? "secondary" : "primary"}
            size="small"
            onClick={() => isAssumedOrg ? handleSwitchBack() : handleSwitchTo(params.row.id)}
          >
            {isAssumedOrg ? t('organizations.switchBack', 'Switch Back') : t('organizations.switchTo', 'Switch To')}
          </Button>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Calculate count excluding "Band Space Org"
  const organizationCount = organizations.length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1400, width: '100%', mx: 'auto' }}>
      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" component="h1">
            {t('organizations.title', 'Organizations')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {organizationCount} {organizationCount === 1 ? 'organization' : 'organizations'}
          </Typography>
        </Box>
        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Mobile List View */}
        {isMobile ? (
          <>
            {organizations.length === 0 ? (
              <Box sx={{ 
                mt: 4, 
                mb: 4,
                textAlign: 'center',
                py: 6,
                bgcolor: 'background.default',
                borderRadius: 2
              }}>
                <Typography variant="body1" color="text.secondary">
                  {t('organizations.noOrganizations', 'No organizations found')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2, mx: -1 }}>
                {organizations.map((org) => {
                  const isAssumedOrg = org.id === assumedOrg.id;
                  const isUpdating = updatingEnabledStatus === org.id;
                  const isUpdatingExperimental = updatingExperimentalOrgId === org.id;
                  const isUpdatingTest = updatingTestOrgId === org.id;
                  const experimentalOn = org.experimentalFeaturesEnabled === true;
                  const testOn = org.test === true;
                  return (
                    <Box
                      key={org.id}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: isAssumedOrg ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: isAssumedOrg ? 'rgba(79, 95, 235, 0.08)' : 'background.paper',
                        transition: 'all 0.2s ease',
                        boxShadow: isAssumedOrg ? '0 2px 8px rgba(79, 95, 235, 0.15)' : 'none',
                      }}
                    >
                      <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '1.125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              letterSpacing: '-0.01em',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {org.name}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={org.enabled ?? true}
                                  onChange={() => handleToggleEnabled(org.id, org.enabled ?? true)}
                                  disabled={isUpdating}
                                  size="small"
                                  inputProps={{
                                    'aria-label': t('organizations.enabled', 'Enabled'),
                                  }}
                                />
                              }
                              label=""
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={experimentalOn}
                                  onChange={() => handleToggleExperimental(org.id, experimentalOn)}
                                  disabled={isUpdatingExperimental}
                                  size="small"
                                  inputProps={{
                                    'aria-label': t('organizations.experimental', 'Experimental'),
                                  }}
                                />
                              }
                              label=""
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={testOn}
                                  onChange={() => handleToggleTest(org.id, testOn)}
                                  disabled={isUpdatingTest}
                                  size="small"
                                  inputProps={{
                                    'aria-label': t('organizations.test', 'Test'),
                                  }}
                                />
                              }
                              label=""
                            />
                          </Stack>
                        </Stack>
                        
                        <Divider sx={{ mb: 1 }} />

                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                            }}
                          >
                            {t('organizations.stripeConnect.column', 'Stripe Connect')}:
                          </Typography>
                          {renderStripeConnectCell(org)}
                        </Stack>
                        
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            mb: 1,
                          }}
                        >
                          {t('organizations.createdAt', 'Created at')}: {org.createdAt ? format(new Date(org.createdAt), 'MMM d, yyyy HH:mm') : ''}
                        </Typography>
                        
                        <Button
                          variant={isAssumedOrg ? "contained" : "outlined"}
                          color={isAssumedOrg ? "secondary" : "primary"}
                          size="small"
                          onClick={() => isAssumedOrg ? handleSwitchBack() : handleSwitchTo(org.id)}
                          fullWidth
                        >
                          {isAssumedOrg ? t('organizations.switchBack', 'Switch Back') : t('organizations.switchTo', 'Switch To')}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : (
          /* Desktop Table View */
          <DataGrid
            rows={organizations}
            columns={columns}
            autoHeight
            hideFooter
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeader': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        )}
      </Card>
    </Box>
  );
};

export default OrganizationsPage; 